import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  useFetchAuthorisation,
  authorisationConverter,
  useFetchUserData,
} from "../../utils/AppFunctions";
import config from "../../app-config";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "../../layouts/AdminLayout";
import { MdAdd } from "react-icons/md";
import { create_new_account, delete_account, fetch_account_by_id, update_account } from "../../api/accountsAPI";
import { fetch_all_users_admin } from "../../api/usersAPI";
import Select from 'react-select'
import notify from "../../utils/IANotification";
import ModalLayout from "../../layouts/ModalLayout";

export default function AccountsFormPage() {
  const navigate = useNavigate();
  let account_id = new URLSearchParams(window.location.search).get("account_id") || "new";
  const [deleteConfirmation, setDeleteConfirmation] = useState("")
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)
  const [accountData, setAccountData] = useState({
    name: "",
    description: "",
    currency: [], // [{"currency": "", rate: 1}
    member_user_ids: []
  })

  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)


  const fetchAccountById = async () => {
    if (account_id === "new") return;
    let { data, error, message } = await fetch_account_by_id(account_id);
    if (error) return console.log("Error fetching account:", message);
    // Ensure member_user_ids exists for compatibility
    setAccountData({ ...data, member_user_ids: data.member_user_ids || [] });
  }

  useEffect(() => {
    fetchAccountById();
    fetchPlatformUsers();
  }, [account_id]);

  const fetchPlatformUsers = async () => {
    setLoading(true)
    const data = await fetch_all_users_admin()
    if (data?.status === 200) {
      setUsers(data.data)
    }
    setLoading(false)
  }

  const updateAccountById = async () => {
    if (account_id === "new") return;
    let { data, error, message } = await update_account(account_id, accountData);
    if (error) return notify({ type: "danger", message: `Error updating account: ${message}` });
    await fetchAccountById();
    notify({ type: "success", message: "Account updated successfully" });
  }

  const createAccount = async () => {
    let { data, error, message } = await create_new_account(accountData);
    if (error) return notify({ type: "danger", message: `Error creating account: ${message}` });
    notify({ type: "success", message: "Account created successfully" });
    navigate(`/admin/accounts?account_id=${data.id}`);
  }

  const deleteAccountById = async () => {
    let { data, error, message } = await delete_account(account_id);
    if (error) return notify({ type: "danger", message: `Error deleting account: ${message}` });
    notify({ type: "success", message: "Account deleted successfully" });
    navigate(`/admin/accounts`);
  }

  return (
    <AdminLayout navSelected={"Accounts"}
      toolbar={
        <>
          <h1 className="text-2xl font-bold text-gray-900">
            {account_id === "new" ? "Create Account" : "Edit Account"}
          </h1>
          <div className="flex gap-2">
            {account_id !== "new" && (
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                onClick={() => setShowDeleteConfirmationModal(true)}
              >
                Delete
              </button>
            )}
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              onClick={() => {
                if (account_id === "new") {
                  createAccount();
                } else {
                  updateAccountById();
                }
              }}
            >
              {account_id === "new" ? "Create" : "Save"}
            </button>
          </div>
        </>
      }
    >
      <ModalLayout modalTitle={`Delete Account: ${accountData?.account_name || ""}`}
        isVisible={showDeleteConfirmationModal} setIsVisible={setShowDeleteConfirmationModal}>
        <div className="input-container">
          <p className="input-label">There is no undo for this action. This will permanently delete the account. By doing so, all claims and related data will also be removed.</p>
          <p className="input-label">To confirm deletion, please type 'delete'</p>
          <input type="text" className="text-input" value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} />
        </div>
        <div className="flex-row flex justifyEnd">
          <button className="button-delete" onClick={() => {
            if (deleteConfirmation === "delete") {
              deleteAccountById()
            } else {
              notify({ type: "error", message: "Please type 'delete' to confirm deletion" })
            }

          }}>Delete</button>
        </div>
      </ModalLayout>
      <div className="flex flex-row gap-2 items-start">
        <div className="w-full">
          <div className="card">
            <p className="card-title">Account Information</p>
            <div className="input-container">
              <label className="input-label">Name</label>
              <input type="text" className="text-input" placeholder="e.g. Missions" value={accountData.name} onChange={(e) => setAccountData({ ...accountData, name: e.target.value })} />
            </div>
            <div className="input-container">
              <label className="input-label">Description</label>
              <textarea className="textarea-input h-24" placeholder="e.g. This budget is for the main ministry as a whole" value={accountData.description} onChange={(e) => setAccountData({ ...accountData, description: e.target.value })} />
            </div>
          </div>
          <div className="card mt-2">
            <p className="card-title">Account Members</p>
            <div className="input-container">
              <label className="input-label">Users</label>
              <Select
                value={users.filter(user => (accountData.member_user_ids || []).includes(user.id)).map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name} (${user.email})` }))}
                options={users.map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name} (${user.email})` }))}
                isMulti
                styles={{ container: (base) => ({ ...base, width: "100%" }) }}
                onChange={(selected) => setAccountData({ ...accountData, member_user_ids: selected.map(s => s.value) })}
                placeholder="Select users allowed to submit claims for this account"
              />
            </div>
          </div>
        </div>
        <div className="card">
          <p className="card-title">Secondary Currencies</p>
          <div className="flex flex-col gap-4">
            {accountData.currency.map((currencyItem, index) => (
              <div key={index} className="flex flex-row gap-2 items-start">
                <input type="text" className="text-input flex-1" placeholder="Currency Code (e.g. USD)" value={currencyItem.currency} onChange={(e) => {
                  let newCurrency = [...accountData.currency];
                  newCurrency[index].currency = e.target.value;
                  setAccountData({ ...accountData, currency: newCurrency });
                }} />
                <div>
                  <input type="number" className="text-input flex-1" placeholder="Exchange Rate" value={currencyItem.rate} onChange={(e) => {
                    let newCurrency = [...accountData.currency];
                    newCurrency[index].rate = parseFloat(e.target.value);
                    setAccountData({ ...accountData, currency: newCurrency });
                  }} />
                  <p className="input-label">1 SGD to {currencyItem.rate} {currencyItem.currency}</p>
                </div>
                <button className="button-delete" onClick={() => {
                  let newCurrency = accountData.currency.filter((_, i) => i !== index);
                  setAccountData({ ...accountData, currency: newCurrency });
                }}>Delete</button>
              </div>
            ))}
            <button className="button-add flex items-center gap-1 justify-center" onClick={() => {
              let newCurrency = [...accountData.currency, { currency: "", rate: 1 }];
              setAccountData({ ...accountData, currency: newCurrency });
            }}>
              <MdAdd size={20} />
              <span>Add Currency</span>
            </button>
          </div>
        </div>

      </div>

      {/* <pre>{JSON.stringify(accountData, null, 2)}</pre> */}
    </AdminLayout>
  )
}